import { DASHBOARD_MESSAGES } from "@/modules/Dashboards/AdminDashboard/constants/AdminDashboardConstants";
import { CollectionFilter } from "@/modules/DashboardTemplate/models/CollectionFilter";
import {
  AxisXY,
  OptionsChart
} from "@/modules/DashboardTemplate/models/optionsChart";
import { PropertiesChart } from "@/modules/DashboardTemplate/models/PropertiesChart";
import { SectionOfDashboard } from "@/modules/DashboardTemplate/models/SectionOfDashboard";
import { Utils } from "@/shared/utils/utils";
import { Component, Inject, Prop, Vue, Watch } from "vue-property-decorator";
import { mapGetters } from "vuex";
import {
  GRID_CHART_WITH_LEGEND,
  LEGEND_CHART,
  TITLE_CHART
} from "../ChartsConstants";
import { DynamicSelectData } from "@/shared/components/DynamicSelect/model/DynamicSelectData";
import { getUserByDocument } from "@/modules/DashboardTemplate/Utils";
import { UserService } from "@/modules/User/services/UserService";

@Component({
  computed: {
    ...mapGetters({
      sectionsOfDashboardStore: "getSectionsOfdashboard",
      refreshCharts: "getRefreshCharts"
    })
  }
})
export default class StackedBarChart extends Vue {
  sectionsOfDashboardStore!: Array<SectionOfDashboard>;
  refreshCharts!: number;
  EMPTY_STATE_HEADER = DASHBOARD_MESSAGES.NO_RESULTS;
  EMPTY_STATE_PARAGRAPH = DASHBOARD_MESSAGES.NOT_FOUND;

  showMenu = false;
  menuX = 0;
  menuY = 0;
  selectedBarData: any = null;
  tooltipParams: any = null; // Guardar los parámetros del tooltip

  @Inject() userService!: UserService;

  @Prop() propertiesToChart!: PropertiesChart;
  @Prop() dataToGraph?: CollectionFilter;

  chartOptions: OptionsChart = new OptionsChart();

  mounted() {
    this.chart();

    this.$nextTick(() => {
      const chartRef = this.$refs.myChart as any;
      if (chartRef && chartRef.$el) {
        chartRef.$el.addEventListener("contextmenu", (e: MouseEvent) => {
          e.preventDefault();
          this.handleRightClick(e);
        });
      }
    });
  }

  beforeDestroy() {
    const chartRef = this.$refs.myChart as any;
    if (chartRef && chartRef.$el) {
      chartRef.$el.removeEventListener("contextmenu", this.handleRightClick);
    }
  }

  @Watch("refreshCharts")
  @Watch("propertiesToChart", { deep: true })
  onChangeData() {
    this.chart();
  }

  chart() {
    if (!this.dataToGraph) return;
    const dataToGraph = this.dataToGraph;
    const chartData = dataToGraph.chartData;

    const series = chartData.data.map((item: any) => {
      return {
        name: item.name,
        type: "bar",
        stack: item.stack,
        barWidth: "10%",
        label: {
          show: item.labelBar ? true : false,
          formatter: item.labelBar,
          position: "top",
          color: "black"
        },
        data: item.data
      };
    });
    const groups = Object.values(dataToGraph.chartData.groups);

    let yAxis: AxisXY = { type: "value" };
    let xAxis: AxisXY = {
      type: "category",
      data: dataToGraph.chartData.xAxis,
      axisLabel: {
        rotate: 45
      }
    };
    if (this.propertiesToChart.chartType.includes("horizontal")) {
      yAxis = { type: "category", data: dataToGraph.chartData.xAxis };
      xAxis = {
        type: "value",
        axisLabel: {
          rotate: 45
        }
      };
    }

    const color = Utils.generateColorsTones(
      this.propertiesToChart.chartColor.hex,
      6
    );
    this.chartOptions = {
      color,
      tooltip: { trigger: "item" },
      title: {
        ...TITLE_CHART,
        text: dataToGraph.chartName
      },
      legend: {
        data: groups[groups.length - 1],
        ...LEGEND_CHART
      },
      grid: {
        ...GRID_CHART_WITH_LEGEND
      },
      xAxis,
      yAxis,
      series
    };
  }

  handleClick(event: any) {
    this.$emit("click", {
      chartType: "stackedColumn",
      event
    });
  }

  handleRightClick(e: MouseEvent) {
    e.preventDefault();
    const allowedGroupBy = ["document", "owner", "user", "userId"];
    const groupBy = (this.propertiesToChart
      .groupBy as DynamicSelectData[])?.find((group: any) =>
      allowedGroupBy.includes(group.id)
    );

    if (!groupBy) {
      return;
    }

    // Obtener los datos del gráfico en el momento del click
    const chartRef = this.$refs.myChart as any;
    if (!chartRef || !chartRef.chart) return;

    const chart = chartRef.chart;

    // Obtener la posición del mouse relativa al canvas
    const canvas = chart.getDom().querySelector("canvas");
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Detectar si el click fue sobre una barra
    const params = chart.getZr().handler.findHover(x, y);

    if (params && params.target && params.target.dataIndex !== undefined) {
      // Construir el objeto con los datos del elemento clickeado
      const dataIndex = params.target.dataIndex;
      const seriesIndex = params.target.seriesIndex || 0;
      const series = chart.getOption().series[seriesIndex];

      this.tooltipParams = {
        seriesName: series.name,
        name: this.dataToGraph?.chartData.xAxis[dataIndex],
        value: series.data[dataIndex],
        dataIndex: dataIndex,
        seriesIndex: seriesIndex,
        data: series.data[dataIndex]
      };

      this.selectedBarData = this.tooltipParams;

      // Ocultar el tooltip
      chart.dispatchAction({ type: "hideTip" });

      // Mostrar el menú contextual
      this.menuX = e.clientX;
      this.menuY = e.clientY;
      this.showMenu = true;
    }
  }

  detectDocument() {
    const allowedGroupBy = ["document", "owner", "user", "userId"];
    const groupById = (this.propertiesToChart.groupBy[0] as DynamicSelectData)
      ?.id;

    if (groupById && allowedGroupBy.includes(groupById)) {
      return this.selectedBarData ? this.selectedBarData.name : "";
    }
    if (this.selectedBarData && this.selectedBarData.seriesName) {
      if (this.selectedBarData.seriesName.split("-").length > 1) {
        const potentialNumber = this.selectedBarData.seriesName.split("-");
        if (!isNaN(Number(potentialNumber[1].trim()))) {
          return potentialNumber[1].trim();
        }
        return potentialNumber[0].trim();
      }
      return this.selectedBarData.seriesName;
    }
    return "";
  }

  async onMenuAction() {
    const userOrId = this.detectDocument();
    // Buscar usuario usando la función de utils
    // El nombre del eje X está en tooltipParams.name
    const userId = await getUserByDocument(userOrId, this.userService);
    // Si se encontró el usuario, abrir su perfil en nueva pestaña
    if (userId) {
      const route = this.$router.resolve({ path: `/profile/${userId}` });
      window.open(route.href, "_blank");
      this.showMenu = false;
      return;
    }

    const route = this.$router.resolve({ path: `/profile/${userOrId}` });
    window.open(route.href, "_blank");
    // Cerrar el menú contextual
    this.showMenu = false;
  }
}
